// Étape 11 — Composer la proposition (consultant) : infos obligatoires, sélection libre, règles, validations, aperçu, envoi
import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { C, Btn, Input, Select, FormField, Alert, prestationsApi, fmtEur } from "./shared";
import { SuiviPaiement } from "./SuiviPaiement";
import { EnvoisProposition } from "./EnvoisProposition";
import { MissionDetail, Chronologie } from "./Missions";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const hdr = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("auprea_token")}` } });
const eur = fmtEur;
const SUJETS = ["Valorisation", "Statuts et pacte d'associés", "Transmission", "Homme-clé et assurance croisée"];
const QTE = ["ATELIER", "AGF", "COORD"];
const STATUTS = { expiree: ["Expirée", C.red], signee: ["Signée", C.green], acompte_paye: ["Acompte payé", C.teal], payee: ["Payée", C.green], retractation_demandee: ["Rétractation demandée", C.red], retractee: ["Rétractée", C.red], brouillon: ["Brouillon", C.mid], en_attente_validation: ["En attente de validation", C.amber], validee: ["Validée par l'admin", C.green], envoyee: ["Envoyée", C.teal] };

const MissionLien = ({ pid }) => {
  const [mid, setMid] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => { axios.get(`${API}/parcours/${pid}/mission`, hdr()).then(r => setMid(r.data.id)).catch(() => {}); }, [pid]);
  if (!mid) return null;
  return open ? <MissionDetail mid={mid} onBack={() => setOpen(false)} /> : <Btn variant="gold" onClick={() => setOpen(true)} data-testid="compo-ouvrir-mission">🧭 Ouvrir la mission</Btn>;
};

const Box = ({ title, children, testid }) => (
  <div data-testid={testid} style={{ background: "#fff", border: `.5px solid ${C.border}`, borderRadius: 12, padding: 18, marginBottom: 14 }}>
    <div style={{ fontSize: 13, fontWeight: 700, color: C.navy, marginBottom: 12, fontFamily: "'Playfair Display', serif" }}>{title}</div>
    {children}
  </div>
);

const Infos = ({ d, set, pro }) => {
  const cli = d.client || {}, ent = d.entreprise || {};
  const sc = (k, v) => set({ client: { ...cli, [k]: v } });
  const se = (k, v) => set({ entreprise: { ...ent, [k]: v } });
  return (
    <Box title="1. Informations complémentaires (obligatoires avant l'envoi)" testid="compo-infos">
      <div style={{ display: "grid", gridTemplateColumns: "140px 1fr 200px", gap: 10 }}>
        <FormField label="Civilité"><Select value={cli.civilite || ""} onChange={(e) => sc("civilite", e.target.value)} data-testid="compo-civilite">
          <option value="">—</option><option>Mme</option><option>M.</option></Select></FormField>
        <FormField label="Adresse postale complète"><Input value={cli.adresse || ""} onChange={(e) => sc("adresse", e.target.value)} placeholder="N°, rue, code postal, commune" data-testid="compo-adresse" /></FormField>
        <FormField label="Format du rendez-vous"><Select value={cli.format_rdv || ""} onChange={(e) => sc("format_rdv", e.target.value)} data-testid="compo-format">
          <option value="">—</option><option value="presentiel">Présentiel</option><option value="visio">Visioconférence</option></Select></FormField>
      </div>
      {pro && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginTop: 6 }}>
          {[["raison_sociale", "Raison sociale"], ["forme", "Forme juridique"], ["siren", "SIREN"], ["adresse", "Adresse du siège"], ["qualite_signataire", "Qualité du signataire (Gérant, Président…)"]].map(([k, l]) => (
            <FormField key={k} label={l}><Input value={ent[k] || ""} onChange={(e) => se(k, e.target.value)} data-testid={`compo-ent-${k}`} /></FormField>
          ))}
          <FormField label="Effectif exact (salariés)"><Input type="number" value={ent.effectif ?? ""} onChange={(e) => se("effectif", e.target.value === "" ? "" : Number(e.target.value))} data-testid="compo-ent-effectif" /></FormField>
          <label style={{ gridColumn: "1/-1", fontSize: 12, display: "flex", gap: 6, alignItems: "center" }}>
            <input type="checkbox" checked={!!d.activite_principale} onChange={(e) => set({ activite_principale: e.target.checked })} data-testid="compo-activite-principale" />
            L'objet de la mission relève de l'activité principale de l'entreprise (désactive la rétractation — à justifier)
          </label>
          {d.activite_principale && <Input style={{ gridColumn: "1/-1" }} value={d.activite_justification || ""} placeholder="Justification (tracée dans le journal)" onChange={(e) => set({ activite_justification: e.target.value })} data-testid="compo-activite-justif" />}
        </div>
      )}
      <FormField label="Votre demande (pré-rempli à partir du bilan, modifiable)">
        <textarea value={d.demande_client || ""} onChange={(e) => set({ demande_client: e.target.value })} data-testid="compo-demande"
          style={{ width: "100%", minHeight: 70, border: `1px solid ${C.border}`, borderRadius: 8, padding: 8, fontSize: 13, fontFamily: "inherit" }} />
      </FormField>
    </Box>
  );
};

const Lignes = ({ d, set, catalogue, formules, pro }) => {
  const [q, setQ] = useState("");
  const [pub, setPub] = useState(pro ? "dirigeant" : "particulier");
  const calcBy = Object.fromEntries((d.calc?.lignes || []).map(l => [l.code + (l.sujet || ""), l]));
  const upd = (i, patch) => set({ lignes: d.lignes.map((l, j) => j === i ? { ...l, ...patch } : l) });
  const dispo = catalogue.filter(p => ["paiement_unique", "abonnement_mensuel"].includes(p.type) && p.actif
    && (pub === "tous" || p.public === pub || p.public === "tous") && (!q || p.nom.toLowerCase().includes(q.toLowerCase()) || p.code.toLowerCase().includes(q.toLowerCase())));
  const partir = (k) => set({ lignes: (formules?.[k]?.lignes || []).filter(l => l.code && !l.isOrientation && !String(l.code).startsWith("REM")).map(l => ({ code: l.code, qte: 1, pourquoi: l.pourquoi || "", sujet: null, prix: null })) });
  return (
    <Box title="2. Sélection des offres" testid="compo-lignes">
      {formules && (
        <div style={{ display: "flex", gap: 6, marginBottom: 12, alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ fontSize: 12, color: C.mid }}>Partir d'une formule :</span>
          {[["essentiel", "Essentiel"], ["recommande", "Recommandé"], ["complet", "Complet"]].map(([k, l]) => formules[k] && (
            <Btn key={k} small onClick={() => partir(k)} data-testid={`compo-partir-${k}`}>{l}</Btn>
          ))}
        </div>
      )}
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <tbody>
          {d.lignes.map((l, i) => {
            const p = catalogue.find(x => x.code === l.code) || {};
            const c = calcBy[l.code + (l.sujet || "")] || {};
            const fm = p.fiche_mission || {};
            return (
              <tr key={i} style={{ borderTop: `.5px solid ${C.border}` }} data-testid={`compo-ligne-${i}`}>
                <td style={{ padding: "8px 4px" }}>
                  <div style={{ fontWeight: 600, color: C.navy }}>{p.nom || l.code}</div>
                  {l.pourquoi && <div style={{ fontSize: 11, color: C.teal }}>Pourquoi : {l.pourquoi}</div>}
                  {l.code === "COORD" && (
                    <Select value={l.sujet || ""} onChange={(e) => upd(i, { sujet: e.target.value || null })} data-testid={`compo-sujet-${i}`} style={{ marginTop: 4, borderColor: l.sujet ? C.border : C.red }}>
                      <option value="">Sujet (obligatoire)…</option>{SUJETS.map(s => <option key={s}>{s}</option>)}
                    </Select>
                  )}
                </td>
                <td style={{ width: 90 }}>{QTE.includes(l.code) && <Input type="number" min="1" value={l.qte} onChange={(e) => upd(i, { qte: Math.max(1, Number(e.target.value)) })} data-testid={`compo-qte-${i}`} />}</td>
                <td style={{ width: 130 }}>{fm.prix_libre && <Input type="number" value={l.prix ?? fm.prix_min ?? ""} onChange={(e) => upd(i, { prix: e.target.value === "" ? null : Number(e.target.value) })} placeholder={`min ${fm.prix_min} €`} data-testid={`compo-prix-${i}`} />}</td>
                <td style={{ width: 110, textAlign: "right", fontWeight: 700 }}>{c.ttc != null ? eur(c.ttc) : "…"}{c.abo ? " /mois" : ""}</td>
                <td style={{ width: 40, textAlign: "right" }}><Btn small variant="red" onClick={() => set({ lignes: d.lignes.filter((_, j) => j !== i) })} data-testid={`compo-retirer-${i}`}>✕</Btn></td>
              </tr>
            );
          })}
          {(d.calc?.lignes || []).filter(l => l.remise_auto || l.code === "REMISE").map(l => (
            <tr key={l.code} style={{ borderTop: `.5px solid ${C.border}`, color: C.green }}><td colSpan={3} style={{ padding: "8px 4px" }}>{l.nom}</td><td style={{ textAlign: "right", fontWeight: 700 }}>{eur(l.ttc)}</td><td /></tr>
          ))}
        </tbody>
      </table>
      <div style={{ display: "flex", gap: 8, marginTop: 12, alignItems: "center" }}>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ajouter une prestation du catalogue…" data-testid="compo-recherche" />
        <Select value={pub} onChange={(e) => setPub(e.target.value)} style={{ width: 160 }} data-testid="compo-public">
          <option value="particulier">Particuliers</option><option value="dirigeant">Dirigeants</option><option value="tous">Tout le catalogue</option>
        </Select>
      </div>
      {q && (
        <div style={{ border: `.5px solid ${C.border}`, borderRadius: 8, marginTop: 6, maxHeight: 220, overflowY: "auto" }}>
          {dispo.map(p => (
            <div key={p.code} onClick={() => { set({ lignes: [...d.lignes, { code: p.code, qte: 1, pourquoi: "", sujet: null, prix: null }] }); setQ(""); }}
              data-testid={`compo-ajouter-${p.code}`} style={{ padding: "8px 12px", cursor: "pointer", fontSize: 13, display: "flex", justifyContent: "space-between", borderBottom: `.5px solid ${C.border}` }}>
              <span><strong>{p.code}</strong> — {p.nom}</span><span style={{ color: C.mid }}>{p.prix_affiche}</span>
            </div>
          ))}
          {!dispo.length && <div style={{ padding: 10, fontSize: 12, color: C.muted }}>Aucune prestation</div>}
        </div>
      )}
      {d.calc && (
        <div data-testid="compo-totaux" style={{ marginTop: 14, marginLeft: "auto", width: 300, fontSize: 13 }}>
          {[["Total HT", d.calc.total_ht], [`TVA ${String(d.calc.tva_taux).replace(".", ",")} %`, d.calc.tva_montant]].map(([l, v]) => (
            <div key={l} style={{ display: "flex", justifyContent: "space-between", padding: "3px 0" }}><span>{l}</span><span>{eur(v)}</span></div>
          ))}
          <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 10px", background: C.gold, color: "#fff", fontWeight: 700, borderRadius: 6 }} data-testid="compo-total-ttc">
            <span>TOTAL TTC</span><span>{eur(d.calc.total_ttc)}</span></div>
          {d.calc.mensuel > 0 && <div style={{ textAlign: "right", fontSize: 12, color: C.mid, marginTop: 4 }} data-testid="compo-mensuel">puis {eur(d.calc.mensuel)} / mois</div>}
        </div>
      )}
    </Box>
  );
};

const Demandes = ({ d, onDemande }) => {
  const [rem, setRem] = useState({ type: "montant", valeur: "", motif: "" });
  const [dx, setDx] = useState({ jalon: "", motif: "" });
  const [open, setOpen] = useState("");
  const st = (x) => x ? ({ en_attente: "en attente", acceptee: "acceptée", refusee: "refusée" }[x.statut] || x.statut) : null;
  return (
    <Box title="3. Demandes soumises à validation de l'admin" testid="compo-demandes">
      {d.remise && <div style={{ fontSize: 12, marginBottom: 6 }} data-testid="compo-remise-statut">Remise {d.remise.valeur_accordee ?? d.remise.valeur}{d.remise.type === "pct" ? " %" : " €"} : <strong>{st(d.remise)}</strong>{d.remise.commentaire_admin ? ` — ${d.remise.commentaire_admin}` : ""}</div>}
      {d.deux_fois && <div style={{ fontSize: 12, marginBottom: 6 }} data-testid="compo-2x-statut">Paiement en 2 fois (solde : {d.deux_fois.jalon}) : <strong>{st(d.deux_fois)}</strong>{d.deux_fois.commentaire_admin ? ` — ${d.deux_fois.commentaire_admin}` : ""}</div>}
      <div style={{ display: "flex", gap: 8 }}>
        <Btn small onClick={() => setOpen(open === "r" ? "" : "r")} data-testid="compo-demander-remise">Demander une remise</Btn>
        <Btn small onClick={() => setOpen(open === "d" ? "" : "d")} data-testid="compo-demander-2x">Demander un paiement en 2 fois</Btn>
      </div>
      {open === "r" && (
        <div style={{ display: "grid", gridTemplateColumns: "120px 120px 1fr auto", gap: 8, marginTop: 10 }}>
          <Select value={rem.type} onChange={(e) => setRem({ ...rem, type: e.target.value })} data-testid="remise-type"><option value="montant">Montant €</option><option value="pct">%</option></Select>
          <Input type="number" value={rem.valeur} onChange={(e) => setRem({ ...rem, valeur: e.target.value })} placeholder="Valeur" data-testid="remise-valeur" />
          <Input value={rem.motif} onChange={(e) => setRem({ ...rem, motif: e.target.value })} placeholder="Motif (obligatoire)" data-testid="remise-motif" />
          <Btn small variant="gold" disabled={!rem.motif.trim() || !(Number(rem.valeur) > 0)} onClick={() => { onDemande({ remise: { ...rem, valeur: Number(rem.valeur) } }); setOpen(""); }} data-testid="remise-envoyer">Envoyer la demande</Btn>
        </div>
      )}
      {open === "d" && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 8, marginTop: 10 }}>
          <Input value={dx.jalon} onChange={(e) => setDx({ ...dx, jalon: e.target.value })} placeholder="Étape du solde (ex. remise du dossier de continuité)" data-testid="2x-jalon" />
          <Input value={dx.motif} onChange={(e) => setDx({ ...dx, motif: e.target.value })} placeholder="Motif (obligatoire)" data-testid="2x-motif" />
          <Btn small variant="gold" disabled={!dx.motif.trim() || !dx.jalon.trim()} onClick={() => { onDemande({ deux_fois: dx }); setOpen(""); }} data-testid="2x-envoyer">Envoyer la demande</Btn>
        </div>
      )}
      <div style={{ fontSize: 11, color: C.muted, marginTop: 8 }}>Sans remise ni paiement en 2 fois, vous envoyez directement, sans validation.</div>
    </Box>
  );
};

const Regles = ({ calc }) => {
  const r = calc.regles;
  return (
    <Box title="4. Règles appliquées" testid="compo-regles">
      <div style={{ fontSize: 13, lineHeight: 1.8 }}>
        <div data-testid="regle-retractation">Droit de rétractation : <strong>{r.retractation_applicable ? `oui (${r.delai_retractation} jours)` : "non"}</strong></div>
        <div data-testid="regle-hors-etab">Contrat hors établissement : <strong>{r.hors_etablissement ? "oui" : "non"}</strong></div>
        <div data-testid="regle-lm">Lettre de mission : <strong>{r.lettre_mission ? "oui" : "non (proposition + CGV)"}</strong></div>
      </div>
      {r.hors_etablissement && <Alert type="warn"><span>⚠</span><span data-testid="regle-j7">Rendez-vous en présentiel : le client pourra signer tout de suite, mais le paiement ne lui sera demandé que {r.delai_paiement} jours après sa signature (obligation légale).</span></Alert>}
    </Box>
  );
};

export const ComposerProposition = ({ parcours, formules, onEnvoyee }) => {
  const [d, setD] = useState(null);
  const [catalogue, setCatalogue] = useState([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const pro = parcours.type_parcours === "bilan_dirigeants";
  useEffect(() => {
    axios.get(`${API}/parcours/${parcours.id}/proposition`, hdr()).then(r => setD(r.data));
    prestationsApi.list().then(setCatalogue);
  }, [parcours.id]);
  const body = useMemo(() => d && ({ client: d.client, entreprise: d.entreprise, activite_principale: d.activite_principale,
    activite_justification: d.activite_justification, demande_client: d.demande_client, lignes: d.lignes }), [d]);
  useEffect(() => {
    if (!dirty || !body) return;
    const t = setTimeout(async () => {
      try { const r = await axios.put(`${API}/parcours/${parcours.id}/proposition`, body, hdr()); setD(x => ({ ...x, calc: r.data.calc, statut: r.data.statut, remise: r.data.remise, deux_fois: r.data.deux_fois })); setDirty(false); setErr(""); }
      catch (e) { setErr(e?.response?.data?.detail || "Erreur d'enregistrement"); }
    }, 600);
    return () => clearTimeout(t);
  }, [body, dirty, parcours.id]);
  if (!d) return null;
  const locked = !["brouillon", "validee"].includes(d.statut || "brouillon");
  const signee = ["signee", "acompte_paye", "payee", "retractation_demandee", "retractee"].includes(d.statut);
  const set = (patch) => { if (locked) return; setD({ ...d, ...patch }); setDirty(true); };
  const call = async (fn) => { setBusy(true); setErr(""); try { await fn(); } catch (e) { setErr(e?.response?.data?.detail || "Erreur"); } setBusy(false); };
  const demande = (payload) => call(async () => { const r = await axios.post(`${API}/parcours/${parcours.id}/proposition/demande-validation`, payload, hdr()); setD(r.data); });
  const apercu = (doc) => call(async () => {
    const r = await axios.get(`${API}/parcours/${parcours.id}/proposition/apercu/${doc}`, { ...hdr(), responseType: "blob" });
    window.open(URL.createObjectURL(r.data), "_blank");
  });
  const envoyer = () => call(async () => {
    if (!window.confirm("Envoyer la proposition au client ? Elle ne pourra plus être modifiée.")) return;
    const r = await axios.post(`${API}/parcours/${parcours.id}/proposition/envoyer`, {}, hdr());
    const g = await axios.get(`${API}/parcours/${parcours.id}/proposition`, hdr()); setD(g.data);
    onEnvoyee && onEnvoyee(r.data);
  });
  const reemettre = () => call(async () => { const r = await axios.post(`${API}/parcours/${parcours.id}/proposition/reemettre`, {}, hdr()); setD(r.data); });
  const [lbl, col] = STATUTS[d.statut] || STATUTS.brouillon;
  const c = d.calc;
  return (
    <div data-testid="composer" style={{ marginTop: 20 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: C.navy, fontFamily: "'Playfair Display', serif" }}>Composer la proposition{d.numero ? ` N°${d.numero}` : ""}</div>
        <span data-testid="compo-statut" style={{ fontSize: 11, fontWeight: 700, color: col, border: `.5px solid ${col}`, borderRadius: 20, padding: "2px 10px" }}>{lbl}</span>
        {dirty && <span style={{ fontSize: 11, color: C.muted }}>Enregistrement du brouillon…</span>}
      </div>
      {err && <Alert type="error"><span>⚠</span><span data-testid="compo-erreur">{err}</span></Alert>}
      {d.statut === "expiree" && <Alert type="warn"><span>⚠</span><span>Proposition N°{d.numero} expirée. <Btn small variant="gold" onClick={reemettre} data-testid="compo-reemettre">Émettre une nouvelle proposition</Btn></span></Alert>}
      {d.precedente && <div style={{ fontSize: 11, color: C.mid, marginBottom: 6 }} data-testid="compo-precedente">Remplace la proposition N°{d.precedente} (expirée)</div>}
      {d.statut !== "brouillon" && d.numero && <EnvoisProposition parcoursId={parcours.id} refreshKey={d.statut} />}
      {signee && <SuiviPaiement pid={parcours.id} />}
      {signee && <MissionLien pid={parcours.id} />}
      {d.numero && <Chronologie pid={parcours.id} />}
      {locked && <Alert type="info"><span>ℹ</span><span>{d.statut !== "en_attente_validation" ? "Proposition envoyée : elle ne peut plus être modifiée." : "En attente de validation par l'admin : modification impossible pour l'instant."}</span></Alert>}
      <div style={{ pointerEvents: locked ? "none" : "auto", opacity: locked ? .7 : 1 }}>
        <Infos d={d} set={set} pro={pro} />
        <Lignes d={d} set={set} catalogue={catalogue} formules={formules} pro={pro} />
        <Demandes d={d} onDemande={demande} />
      </div>
      {c && <Regles calc={c} />}
      <Box title="5. Aperçu et envoi" testid="compo-envoi">
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
          <Btn small onClick={() => apercu("proposition")} disabled={busy} data-testid="apercu-proposition">Aperçu : proposition</Btn>
          {c?.regles.lettre_mission && <Btn small onClick={() => apercu("lettre_mission")} disabled={busy} data-testid="apercu-lm">Aperçu : lettre de mission</Btn>}
          <Btn small onClick={() => apercu("cgv")} disabled={busy} data-testid="apercu-cgv">Aperçu : CGV</Btn>
        </div>
        {c && (c.manquants.length > 0 || c.erreurs.length > 0) && (
          <div data-testid="compo-bloquants" style={{ fontSize: 12, color: C.red, marginBottom: 10 }}>À compléter avant l'envoi : {[...c.manquants, ...c.erreurs].join(" · ")}</div>
        )}
        <Btn variant="gold" disabled={busy || !c?.peut_envoyer || dirty} onClick={envoyer} data-testid="compo-envoyer">✉ Envoyer au client</Btn>
      </Box>
      {(d.journal || []).length > 0 && (
        <details style={{ fontSize: 11, color: C.mid }} data-testid="compo-journal"><summary>Journal ({d.journal.length})</summary>
          {d.journal.map((j, i) => <div key={i}>{(j.at || "").slice(0, 16).replace("T", " ")} · {j.by} · {j.action} {j.detail ? `— ${j.detail}` : ""}</div>)}
        </details>
      )}
    </div>
  );
};
